import { FC, useEffect, useRef, useCallback, useState } from 'react';

import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import PushPinIcon from '@mui/icons-material/PushPin';
import {
  Box,
  Button,
  Drawer,
  IconButton,
  Typography,
  Stack,
  CircularProgress,
  Avatar,
  Menu,
  MenuItem,
  useTheme,
} from '@mui/material';

import { OfficeChatApi } from '@/api';
import { IcClose } from '@/assets/images';
import { AdditionalInfoInput } from '@/components/CustomForms/AdditionalInfoForm/AdditionalInfoInput';
import { useOfficeChatStore, useSidebarStore, useToastStore } from '@/store';
import { IOfficeChatMessage } from '@/store/store.officeChat';
import { colors, sizes } from '@/theme/variables';

const PANEL_WIDTH = 400;
const POLL_TIMEOUT = 30000; // 30 seconds long polling timeout

interface ChatMessageProps {
  message: IOfficeChatMessage;
  isOwnMessage: boolean;
  isChatAdmin: boolean;
  messageRef?: (el: HTMLDivElement | null) => void;
  onDelete?: (messageId: string) => void;
  onPin?: (messageId: string) => void;
  onUnpin?: (messageId: string) => void;
}

const ChatMessage: FC<ChatMessageProps> = ({
  message,
  isOwnMessage,
  isChatAdmin,
  messageRef,
  onDelete,
  onPin,
  onUnpin,
}) => {
  const theme = useTheme();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [isHovered, setIsHovered] = useState(false);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handlePin = () => {
    handleMenuClose();
    onPin?.(message._id);
  };

  const handleUnpin = () => {
    handleMenuClose();
    onUnpin?.(message._id);
  };

  const handleDelete = () => {
    handleMenuClose();
    onDelete?.(message._id);
  };

  const formatMessageDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const time = date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });

    const startOfToday = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
    );
    const startOfYesterday = new Date(startOfToday);
    startOfYesterday.setDate(startOfYesterday.getDate() - 1);
    const oneWeekAgo = new Date(startOfToday);
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

    if (date >= startOfToday) {
      return time;
    } else if (date >= startOfYesterday) {
      return `Yesterday ${time}`;
    } else if (date >= oneWeekAgo) {
      const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
      return `${dayName} ${time}`;
    } else {
      const monthName = date.toLocaleDateString('en-US', { month: 'short' });
      const day = date.getDate();
      return `${monthName} ${day}, ${time}`;
    }
  };

  // System messages rendered as a centered pill
  if (message.isSystemMessage) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          mb: 2,
        }}
      >
        <Typography
          sx={{
            fontSize: 12,
            color: 'text.secondary',
            backgroundColor: 'background.default',
            borderRadius: 10,
            px: 2,
            py: 0.5,
          }}
        >
          {message.message}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      ref={messageRef}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        display: 'flex',
        flexDirection: isOwnMessage ? 'row-reverse' : 'row',
        alignItems: 'flex-start',
        gap: 1,
        mb: 2,
      }}
    >
      {!isOwnMessage && (
        <Avatar
          sx={{
            width: 32,
            height: 32,
            fontSize: '0.875rem',
            bgcolor: colors.grey[60],
            color: theme.palette.common.white,
          }}
        >
          {message.createdByInitials}
        </Avatar>
      )}
      <Box
        sx={{
          maxWidth: '75%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: isOwnMessage ? 'flex-end' : 'flex-start',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            background: isOwnMessage
              ? colors.grey[60]
              : theme.palette.background.default,
            color: isOwnMessage
              ? theme.palette.common.white
              : theme.palette.text.primary,
            borderRadius: 2,
            px: 1.5,
            pt: 0.75,
            pb: 0.75,
            wordBreak: 'break-word',
          }}
        >
          {isChatAdmin && (
            <Box
              sx={{
                position: 'absolute',
                top: 4,
                right: 4,
                opacity: isHovered || Boolean(menuAnchor) ? 1 : 0,
                transition: 'opacity 0.15s ease-in-out',
              }}
            >
              <IconButton
                size="small"
                onClick={handleMenuOpen}
                sx={{
                  color: isOwnMessage
                    ? 'rgba(255,255,255,0.7)'
                    : theme.palette.text.secondary,
                  p: 0.25,
                  '&:hover': {
                    backgroundColor: isOwnMessage
                      ? 'rgba(255,255,255,0.15)'
                      : theme.palette.action.hover,
                  },
                }}
              >
                <MoreHorizIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                slotProps={{
                  paper: {
                    sx: { minWidth: 160 },
                  },
                }}
              >
                {message.isPinned ? (
                  <MenuItem
                    onClick={handleUnpin}
                    dense
                    sx={{ justifyContent: 'flex-start' }}
                  >
                    Unpin message
                  </MenuItem>
                ) : (
                  <MenuItem
                    onClick={handlePin}
                    dense
                    sx={{ justifyContent: 'flex-start' }}
                  >
                    Pin message
                  </MenuItem>
                )}
                <MenuItem
                  onClick={handleDelete}
                  dense
                  sx={{ color: 'error.main', justifyContent: 'flex-start' }}
                >
                  Delete message
                </MenuItem>
              </Menu>
            </Box>
          )}
          <Typography
            sx={{ fontSize: 13, lineHeight: 1.4, pr: isChatAdmin ? 2.5 : 0 }}
          >
            {message.message}
          </Typography>
          <Typography
            component="span"
            sx={{
              fontSize: 11,
              color: isOwnMessage
                ? 'rgba(255,255,255,0.7)'
                : theme.palette.text.secondary,
              display: 'block',
              textAlign: 'left',
            }}
          >
            {formatMessageDate(message.createdAt)}
            {!isOwnMessage && (
              <>
                {' • '}
                <Typography
                  component="span"
                  sx={{
                    fontSize: 12,
                    color: 'text.secondary',
                  }}
                >
                  {message.createdByName}
                </Typography>
              </>
            )}
            {message.isEdited && ' (edited)'}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export const OfficeChatPanel: FC = () => {
  const panelRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const pollingRef = useRef<boolean>(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const messageRefsMap = useRef<Map<string, HTMLDivElement>>(new Map());
  const lastPollTimeRef = useRef<number>(Date.now());

  const { isOpen: sidebarOpen } = useSidebarStore();
  const {
    isPanelOpen,
    closePanel,
    messages,
    pinnedMessage,
    setPinnedMessage,
    isLoading,
    setIsLoading,
    setMessages,
    addMessages,
    setIsPolling,
    hasAccess,
    isChatAdmin,
    currentUserId,
    removeMessage,
    removeMessages,
    unreadCount,
    markAsViewed,
    resetUnread,
  } = useOfficeChatStore();

  const { updateToast } = useToastStore();

  const sidebarWidth = sidebarOpen ? sizes.sidebarOpen : sizes.sidebarClosed;

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const scrollToMessage = useCallback((messageId: string) => {
    const el = messageRefsMap.current.get(messageId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Briefly highlight the message
      el.style.transition = 'background-color 0.3s ease';
      el.style.backgroundColor = 'rgba(100, 116, 139, 0.15)';
      setTimeout(() => {
        el.style.backgroundColor = '';
      }, 1500);
    }
  }, []);

  // Load initial messages and start polling when user has access AND currentUserId is set
  useEffect(() => {
    if (!hasAccess || !currentUserId) return;

    const init = async () => {
      if (messages.length === 0) {
        await loadInitialMessages();
      }
      // Start polling only after initial messages are loaded
      startPolling();
    };
    init();

    return () => {
      stopPolling();
    };
  }, [hasAccess, currentUserId]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Handle clicking outside the panel to dismiss it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isPanelOpen &&
        panelRef.current &&
        !panelRef.current.contains(event.target as Node)
      ) {
        // Ignore clicks inside any MUI portal (Menu, Popover, Dialog, etc.)
        // Use closest() instead of querySelector().contains() to avoid false negatives
        // when multiple MUI portals are present in the DOM simultaneously (e.g. on
        // complex form routes that keep Popover nodes mounted).
        const target = event.target as Element;
        if (target.closest?.('.MuiPopover-root, .MuiModal-root')) {
          return;
        }

        // Check if the click is not on the office chat button
        const officeChatButton = document.querySelector(
          '[data-testid="office-chat-button"]',
        );
        if (
          !officeChatButton ||
          !officeChatButton.contains(event.target as Node)
        ) {
          handleClose();
        }
      }
    };

    if (isPanelOpen) {
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 100);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isPanelOpen]);

  const loadInitialMessages = async () => {
    setIsLoading(true);
    try {
      const response = await OfficeChatApi.getMessages(50);
      setMessages(response.data.messages);
      if (response.data.pinnedMessage !== undefined) {
        setPinnedMessage(response.data.pinnedMessage ?? null);
      }
    } catch (error) {
      updateToast({
        open: true,
        message: 'Failed to load Office chat messages',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const startPolling = () => {
    if (pollingRef.current) return;
    pollingRef.current = true;
    setIsPolling(true);
    poll();
  };

  const stopPolling = () => {
    pollingRef.current = false;
    setIsPolling(false);
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  };

  const poll = async () => {
    if (!pollingRef.current) return;

    try {
      abortControllerRef.current = new AbortController();
      // Snapshot the poll start time before the request — sent to the backend so it
      // uses this as the lower bound for deleted-message queries, preventing the same
      // deletions from being returned on every subsequent poll.
      const pollStartTime = Date.now();
      lastPollTimeRef.current = pollStartTime;
      // Get current lastMessageId from store to avoid stale closure
      const currentLastMessageId = useOfficeChatStore.getState().lastMessageId;
      const response = await OfficeChatApi.poll(
        currentLastMessageId,
        POLL_TIMEOUT,
        pollStartTime,
      );

      if (!pollingRef.current) return;

      if (response.data.messages && response.data.messages.length > 0) {
        addMessages(response.data.messages);
      }

      if (
        response.data.deletedMessageIds &&
        response.data.deletedMessageIds.length > 0
      ) {
        removeMessages(response.data.deletedMessageIds);
      }

      // Update pinned message from poll response
      if (response.data.pinnedMessage !== undefined) {
        setPinnedMessage(response.data.pinnedMessage ?? null);
      }

      // Continue polling
      if (pollingRef.current) {
        poll();
      }
    } catch (error) {
      // Only retry if we're still supposed to be polling and it wasn't an abort
      if (
        pollingRef.current &&
        !(error instanceof DOMException && error.name === 'AbortError')
      ) {
        // don't show polling errors
        // console.error('Polling error:', error);
        // updateToast({ open: true, message: 'Polling error, retrying...' });
        // Retry after a short delay on error
        setTimeout(() => {
          if (pollingRef.current) {
            poll();
          }
        }, 5000);
      }
    }
  };

  const handleClose = () => {
    closePanel();
  };

  const handleBackdropClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget) {
      handleClose();
    }
  };

  const handleDeleteMessage = async (messageId: string) => {
    try {
      await OfficeChatApi.deleteMessage(messageId);
      removeMessage(messageId);
    } catch (error) {
      updateToast({ open: true, message: 'Failed to delete message' });
    }
  };

  const handlePinMessage = async (messageId: string) => {
    try {
      const response = await OfficeChatApi.pinMessage(messageId);
      setPinnedMessage(response.data.message);
      // Update isPinned flag on the message in the list
      const store = useOfficeChatStore.getState();
      const msg = store.messages.find((m) => m._id === messageId);
      if (msg) {
        store.updateMessage(messageId, { ...msg, isPinned: true });
      }
      // Clear isPinned on previously pinned messages in the list
      store.messages.forEach((m) => {
        if (m._id !== messageId && m.isPinned) {
          store.updateMessage(m._id, { ...m, isPinned: false });
        }
      });
    } catch (error) {
      updateToast({ open: true, message: 'Failed to pin message' });
    }
  };

  const handleUnpinMessage = async (messageId: string) => {
    try {
      await OfficeChatApi.unpinMessage(messageId);
      setPinnedMessage(null);
      const store = useOfficeChatStore.getState();
      const msg = store.messages.find((m) => m._id === messageId);
      if (msg) {
        store.updateMessage(messageId, { ...msg, isPinned: false });
      }
    } catch (error) {
      updateToast({ open: true, message: 'Failed to unpin message' });
    }
  };

  const handleSendMessage = async (value: string) => {
    if (!value.trim()) return;

    try {
      await OfficeChatApi.sendMessage(value.trim());
      resetUnread();
    } catch (error) {
      updateToast({ open: true, message: 'Failed to send message' });
    }
  };

  const handleMarkAsViewed = async () => {
    try {
      await markAsViewed();
    } catch (error) {
      updateToast({ open: true, message: 'Failed to mark as viewed' });
    }
  };

  return (
    <Drawer
      anchor="left"
      open={isPanelOpen}
      onClose={handleClose}
      variant="persistent"
      ModalProps={{
        keepMounted: false,
        BackdropProps: {
          onClick: handleBackdropClick,
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            left: sidebarWidth,
          },
        },
      }}
      PaperProps={{
        ref: panelRef,
        sx: {
          width: PANEL_WIDTH,
          height: '100vh',
          backgroundColor: 'background.paper',
          left: sidebarWidth,
          transform: isPanelOpen
            ? 'translateX(0)'
            : `translateX(-${PANEL_WIDTH}px)`,
          transition: 'transform 0.3s ease-in-out',
          zIndex: 5,
        },
      }}
      SlideProps={{
        direction: 'right',
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            p: 3,
            backgroundColor: 'background.paper',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="h6" fontWeight={600} color="text.primary">
            Office chat
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Button
              size="small"
              variant="text"
              onClick={handleMarkAsViewed}
              disabled={unreadCount === 0}
              data-testid="mark-as-viewed-button"
            >
              Mark as Viewed
            </Button>
            <IconButton
              onClick={handleClose}
              size="small"
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <IcClose width={20} height={20} />
            </IconButton>
          </Stack>
        </Stack>

        {/* Pinned Message Banner */}
        {pinnedMessage && (
          <Box
            onClick={() => scrollToMessage(pinnedMessage._id)}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              px: 2,
              py: 1,
              backgroundColor: 'background.default',
              borderBottom: '1px solid',
              borderColor: 'divider',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                alignSelf: 'flex-start',
                backgroundColor: 'action.selected',
                borderRadius: '10px',
                pl: 1,
                pr: 2,
                py: 0.25,
              }}
            >
              <PushPinIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 500,
                  color: 'text.secondary',
                  lineHeight: 1.25,
                }}
              >
                Pinned
              </Typography>
            </Box>
            <Typography
              sx={{
                fontSize: 14,
                color: 'text.primary',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {pinnedMessage.message}
            </Typography>
          </Box>
        )}

        {/* Messages List */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            p: 2,
          }}
        >
          {isLoading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '200px',
              }}
            >
              <CircularProgress size={40} />
            </Box>
          ) : messages.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: '200px',
                px: 3,
                textAlign: 'center',
              }}
            >
              <Typography variant="body1" color="text.secondary" gutterBottom>
                No messages yet
              </Typography>
              <Typography variant="body2" color="text.disabled">
                Start a conversation with your team
              </Typography>
            </Box>
          ) : (
            <>
              {messages.map((message) => (
                <ChatMessage
                  key={message._id}
                  message={message}
                  isOwnMessage={message.createdBy === currentUserId}
                  isChatAdmin={isChatAdmin}
                  messageRef={(el) => {
                    if (el) {
                      messageRefsMap.current.set(message._id, el);
                    } else {
                      messageRefsMap.current.delete(message._id);
                    }
                  }}
                  onDelete={handleDeleteMessage}
                  onPin={handlePinMessage}
                  onUnpin={handleUnpinMessage}
                />
              ))}
              <div ref={messagesEndRef} />
            </>
          )}
        </Box>

        {/* Input Area */}
        <Box
          sx={{
            borderTop: '1px solid',
            borderColor: 'divider',
            p: 2,
            backgroundColor: 'background.paper',
          }}
        >
          <AdditionalInfoInput
            placeholder="Message"
            onSubmit={handleSendMessage}
          />
        </Box>
      </Box>
    </Drawer>
  );
};
